/**
 * The order of a screen's blocks, as arithmetic.
 *
 * Somebody's arrangement is a list of block ids kept in their browser. The
 * screen it describes moves on without it: a block is added, another is
 * retired, and one has nothing to show this morning. Everything here is about
 * reading an old list against today's screen without losing anything, and it
 * is kept apart from the component so it can be tested without a mouse.
 */

/**
 * The order to draw in.
 *
 * What was stored comes first, in the order it was stored. A block the stored
 * list has never heard of goes to the end rather than vanishing, and an id
 * that no longer names a block is dropped. Storage is somebody else's to
 * edit, so anything that is not a list of strings counts as nothing stored.
 */
export function mergeOrder(stored: unknown, ids: readonly string[]): string[] {
  const known = new Set(ids);
  const placed = new Set<string>();
  const order: string[] = [];

  if (Array.isArray(stored)) {
    for (const id of stored) {
      if (typeof id === "string" && known.has(id) && !placed.has(id)) {
        placed.add(id);
        order.push(id);
      }
    }
  }
  for (const id of ids) {
    if (!placed.has(id)) order.push(id);
  }
  return order;
}

/** Takes a block out of the order and puts it back beside another. */
export function place(
  order: readonly string[],
  id: string,
  target: string,
  side: "before" | "after",
): string[] {
  if (id === target || !order.includes(id) || !order.includes(target)) return [...order];
  const rest = order.filter((each) => each !== id);
  const at = rest.indexOf(target) + (side === "after" ? 1 : 0);
  return [...rest.slice(0, at), id, ...rest.slice(at)];
}

/**
 * One step up or down.
 *
 * A step is past the next block the reader can see. A block with nothing to
 * show still holds a place in the order, and stepping past only that would
 * look like a button that did nothing.
 */
export function step(
  order: readonly string[],
  id: string,
  by: -1 | 1,
  shown: readonly string[] = order,
): string[] {
  const visible = order.filter((each) => shown.includes(each));
  const at = visible.indexOf(id);
  const neighbour = at === -1 ? undefined : visible[at + by];
  if (neighbour === undefined) return [...order];
  return place(order, id, neighbour, by === -1 ? "before" : "after");
}

/**
 * Where a dragged block lands.
 *
 * Slots are the gaps between the blocks on screen: slot 0 is above the first,
 * slot 1 between the first and second, and the last is below them all. The
 * two gaps either side of the block being dragged are where it already is,
 * so they return null, and so does anything that is not a gap; the screen
 * draws no landing line for those.
 */
export function dropAt(
  order: readonly string[],
  id: string,
  slot: number,
  shown: readonly string[] = order,
): string[] | null {
  const visible = order.filter((each) => shown.includes(each));
  const at = visible.indexOf(id);
  if (at === -1 || !Number.isInteger(slot) || slot < 0 || slot > visible.length) return null;
  if (slot === at || slot === at + 1) return null;
  return slot === visible.length
    ? place(order, id, visible[visible.length - 1]!, "after")
    : place(order, id, visible[slot]!, "before");
}

/** Whether two orders are the same order. */
export function sameOrder(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((id, index) => id === b[index]);
}
